/* screens/sales/PublicPlansPanel.jsx — вкладка «Публичные» раздела «Тарифы».

   Запрос списка, открытый редактор и создание. Дерево и состояния — PublicPlansTree,
   форма — PublicPlanDrawer. Доступ — public_plans_access (subscriptions.manage_public_plans),
   см. plansAccess.js; сервер всё равно проверяет право сам.

   Открытая правка живёт в адресе (?tab=public&plan=<слаг>) — ссылку на тариф можно
   кинуть коллеге, как во вкладке «Закрытые». Создание — в состоянии экрана: у
   незаполненной формы нет адреса, которым стоило бы делиться. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/salesConsole.js';
import { writeClipboard } from '../academy/clipboard.js';
import { PublicPlansBody } from './PublicPlansTree.jsx';
import { PublicPlanDrawer } from './PublicPlanDrawer.jsx';
import { planParamAfterSave, treeContext, withPlanParam } from './publicPlans.js';

const { useState, useMemo, useCallback } = React;
const { useSearchParams, useNavigate } = RR;
const { useFakeQuery } = hooks;

export function PublicPlansPanel() {
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // salesConsole не бросает на HTTP-кодах — приводим к контракту useFakeQuery сами.
  // Перечитывание после сохранения держит прежнее дерево на экране (loading без
  // data — только первая загрузка), иначе оно мигало бы спиннером.
  const query = useFakeQuery('console-public-plans', async () => {
    const res = await api.listPublicPlans();
    if (!res.ok) {
      throw Object.assign(new Error((res.data && res.data.detail) || 'Не удалось загрузить тарифы.'),
        { status: res.status });
    }
    return api.publicPlansOf(res.data);
  }, []);
  const data = query.data || null;
  const ctx = useMemo(() => treeContext(data), [data]);

  const openSlug = params.get('plan') || '';
  const setOpenSlug = useCallback((v) => setParams((prev) => withPlanParam(prev, v)), [setParams]);
  // Пока список не пришёл, тарифа из адреса нет — дровер откроется, когда придёт (S39).
  const editing = data && openSlug ? ctx.bySlug.get(openSlug) || null : null;

  // {mode:'term', baseKey} | {mode:'new'} | {mode:'copy', source}
  const [create, setCreate] = useState(null);

  const copyLink = useCallback(async (url) => {
    const ok = await writeClipboard(url);
    // Без явного «Скопировано» непонятно, сработало ли; при отказе буфера адрес
    // виден в строке — его можно выделить руками.
    toast.show(ok ? 'Скопировано' : 'Не удалось скопировать — выделите адрес в строке и скопируйте вручную',
      ok ? 'success' : 'danger');
  }, [toast]);

  const onSaved = useCallback((saved) => {
    setCreate(null);
    // Созданный тариф снят с продажи — сразу открываем его карточку (по слагу из
    // ОТВЕТА сервера): включить продажу, поставить окно или убрать из витрины
    // удобнее не выходя. После правки карточка закрывается.
    setOpenSlug(planParamAfterSave(saved));
    query.reload();
    toast.show(saved.message, 'success');
  }, [setOpenSlug, query, toast]);

  const utmAction = user && user.utm_access
    ? <Button size="sm" variant="ghost" iconRight="arrow-right" onClick={() => navigate('/console/utm')}>Открыть метки</Button>
    : null;

  return (
    <>
      <PublicPlansBody
        data={data} loading={query.loading && !data} error={query.error} onRetry={query.reload} now={new Date()}
        onNewPlan={() => { setOpenSlug(''); setCreate({ mode: 'new' }); }}
        onNewTerm={(baseKey) => { setOpenSlug(''); setCreate({ mode: 'term', baseKey: baseKey || (ctx.termBases[0] && ctx.termBases[0].value) || '' }); }}
        onEdit={(p) => { setCreate(null); setOpenSlug(p.slug); }}
        onCopyPlan={(p) => { setOpenSlug(''); setCreate({ mode: 'copy', source: p }); }}
        onCopyLink={copyLink}
        utmAction={utmAction}
      />

      {data && editing && !create && (
        <PublicPlanDrawer key={`edit:${editing.slug}`} mode="edit" plan={editing} data={data}
          onClose={() => setOpenSlug('')} onSaved={onSaved}
          onCopyInstead={() => { setOpenSlug(''); setCreate({ mode: 'copy', source: editing }); }} />
      )}
      {data && create && (
        <PublicPlanDrawer key={`${create.mode}:${(create.source && create.source.slug) || create.baseKey || ''}`}
          mode={create.mode} source={create.source} baseKey={create.baseKey} data={data}
          onClose={() => setCreate(null)} onSaved={onSaved} />
      )}
    </>
  );
}
